/**
 * Config store adapter — the only file in this domain that may read the store.
 *
 * Everything downstream reads the merchant's settings through here: the theme, the
 * service list, banners, allowed payment modes and the idle timeout all come from one
 * payload, so there is one place to stub while the API is being built.
 *
 * Where the config comes from: the branch record (`getBranchData`) names a
 * `kioskConfigurationId`, which `saga/outletStatus` stores (`null` when the branch has none).
 * `useKioskConfigFetch` — called once, by `KioskApp` — asks `saga/kioskConfig` to fetch
 * `kioskconfiguration/:id/fetch` when the kiosk mounts, and the saga maps it through
 * `./apiModel`. Until that answers (or for a branch with no configuration) `config` is null and
 * every read below falls back. The slice is persisted, so a restart with no network keeps the
 * last config.
 */
import store from "../../redux-store";
import { subjectFrom } from "../../capabilities/sources/user";

/**
 * ⚠️ The two reads below are `store?.getState?.()`, not the plain `store.getState()` every other
 * core module uses, and the `?.` is the only difference — it is not style.
 *
 * This module is genuinely inside a cycle with the store, on BOTH platforms:
 *   redux-store -> rootSaga -> saga/pos/catalog -> utils/apiService -> kiosk/config -> redux-store
 * (`apiService`'s interceptor imports `isKioskModeEnabled`). While the store module is still
 * evaluating, this file's `store` binding is undefined. Nothing calls into here in that window
 * today — audit checks H and J are 0 — but `isKioskModeEnabled` gates the forced-logout path and
 * the navigation guard, and its contract is that it must NEVER throw and must answer `false`
 * before the store exists. One `?.` keeps that literally true rather than true-by-inspection.
 *
 * It also keeps the FOLDER import (no `/index.desktop`): unlike the rest of `kiosk/*`, this module
 * really is bundled on both platforms — verified in both artifacts, `apiService.native` pulls it
 * in — so it must resolve per platform. `kioskConfigStore` is registered in both stores, so the
 * folder's mobile-first typing costs nothing here.
 */
// A bare constant with no imports of its own — see its header. Importing it here cannot
// widen this module's already-cyclic import graph.
import { KIOSK_ENTRY_ENABLED } from "../featureFlag";
import { buildKioskTheme, sortByDisplayOrder, toIdleOverrides } from "./configModel";
import {
  selectBanners,
  selectConfiguredServices,
  selectContactPolicySetting,
  selectKioskConfig,
  selectKioskEnabled,
  selectKioskPaymentModeIds,
} from "./selectors";
import type {
  KioskBanner,
  KioskConfig,
  KioskContactPolicy,
  KioskImageSlot,
  KioskServiceConfig,
  KioskTheme,
} from "./types";

export * from "./selectors";

/**
 * Is this device in kiosk mode?
 *
 * The one question the POS side asks. It gates the forced-logout path, the navigation
 * guard, and anything else that would otherwise put staff UI in front of a customer, so
 * it must never throw and must answer `false` before the store exists.
 *
 * 🔴 **Entry disabled answers `false` whatever the store says, and that is the whole point.**
 * `enabled` is PERSISTED, so a device that ever ran a build with `KIOSK_ENTRY_ENABLED` on keeps
 * the mode across a restart — and then a build with it off honours the guards while providing no
 * kiosk to be guarded. On web that is unrecoverable through the UI: `history.push`/`replace` are
 * blocked, the `/kiosk` route does not exist, `KioskLock` is a passthrough, so no exit control is
 * rendered anywhere and only clearing localStorage gets the terminal back.
 *
 * `apps/web/src/Routes.js` already pairs its two halves of the door by hand. The other four read
 * sites cannot pair themselves — `history.js`, `helpers/commonHelpers.handleLogoutAction`,
 * `utils/apiService` (both platforms) and mobile's `NavigationUtils` / `kioskLock` /
 * `checkLoginStatus` — so the pairing lives HERE instead, where there is one of it. That makes
 * `featureFlag.ts`'s claim to be "the whole switch" literally true rather than true of three
 * sites out of seven.
 *
 * ⚠️ It does NOT collapse the two questions. `KIOSK_ENTRY_ENABLED` asks "may anyone enter the
 * kiosk on this BUILD"; this asks "is this DEVICE in kiosk mode". The first is a precondition of
 * the second — a device cannot be in a mode the build has no entry to — which is why the
 * implication runs one way and only one way.
 *
 * ⚠️ **A test that needs the mode ON must mock the flag**, not just dispatch
 * `setKioskEnabled(true)`:
 * `jest.mock("@rista/core/kiosk/featureFlag", () => ({ KIOSK_ENTRY_ENABLED: true }))`.
 * Tests that already `jest.mock` this whole module are unaffected.
 */
export function isKioskModeEnabled(): boolean {
  if (!KIOSK_ENTRY_ENABLED) return false;
  const state = store?.getState?.();
  if (!state) return false;
  return selectKioskEnabled(state);
}

/**
 * Whether the logged-in user holds the kiosk licence (`KSK`), decoded by the capabilities' user
 * source (the one reader of `assignedLicence`, audit check O). At call time, never at module eval
 * (see the hard rule above).
 */
export const isAssignedLicenceKSK = (): boolean =>
  subjectFrom(store.getState()?.businessStore?.userData).licence === "KSK";

/** null until the config API has answered at least once on this device. */
export function getKioskConfig(): KioskConfig | null {
  const state = store?.getState?.();
  if (!state) return null;
  return selectKioskConfig(state);
}

/**
 * Always returns a usable palette — falls back to the default brand colour before the
 * config arrives, so the first paint is never unstyled or unreadable.
 */
export function getKioskTheme(): KioskTheme {
  return buildKioskTheme(getKioskConfig()?.primaryColor);
}

export function getConfiguredServices(): KioskServiceConfig[] {
  const state = store?.getState?.();
  return sortByDisplayOrder(state ? selectConfiguredServices(state) : []);
}

/** Reactive twin: `./hooks`'s `useKioskBanners`. Same sort, from the same function. */
export function getBanners(): KioskBanner[] {
  const state = store?.getState?.();
  return sortByDisplayOrder(state ? selectBanners(state) : []);
}

/** Payment modes the merchant allows AT THE KIOSK — a subset of the POS's. */
export function getKioskPaymentModeIds(): string[] {
  const state = store?.getState?.();
  return state ? selectKioskPaymentModeIds(state) : [];
}

/**
 * The merchant's contact setting, RAW. Feeds `contactPolicy()` in `kiosk/customer`, which owns
 * the default; nothing else should read it, and nothing should compare it to a string here.
 */
export function getContactPolicySetting(): KioskContactPolicy | undefined {
  const state = store?.getState?.();
  return state ? selectContactPolicySetting(state) : undefined;
}

/** Feeds `getIdlePolicy(route, overrides)` in kiosk/session. */
export function getIdleOverrides() {
  return toIdleOverrides(getKioskConfig() ?? undefined);
}

export * from "./configModel";
export type {
  KioskBanner,
  KioskConfig,
  KioskContactPolicy,
  KioskImageSlot,
  KioskServiceConfig,
  KioskTheme,
};
